import { useState } from "react";
import { Accordion, Box, Group, Text, TextInput, Textarea, Select, CloseButton, Grid, Divider } from "@mantine/core";
import { Card, Button, Badge, ConfirmModal } from "@xplatform/design-system";
import {
    IconPlus,
    IconSettingsAutomation,
    IconCode,
    IconPrompt,
} from "@tabler/icons-react";
import { useTranslation } from "@hooks/useTranslation";
import type { AgentExecutionFormData } from "@utils/builders/flowConfig";
import type { ProviderViewModel } from "@models/provider";
import type { ModelBasicInfoViewModel } from "@models/model";
import "./AgentExecutionsEditor.scss";

interface AgentExecutionsEditorProps {
    agents: AgentExecutionFormData[];
    llmProviders: ProviderViewModel[];
    llmModels: Record<string, ModelBasicInfoViewModel[]>;
    onAgentsChange: (agents: AgentExecutionFormData[]) => void;
    onLoadModels: (providerKey: string, providerType: "ocr" | "llm") => Promise<void>;
}

const createEmptyAgent = (): AgentExecutionFormData => ({
    domainKey: "",
    userPrompt: "",
    responseSchema: null,
    responseSchemaText: "",
    systemPrompt: "",
    modelName: "",
});

export const AgentExecutionsEditor = ({
    agents,
    llmProviders: _llmProviders,
    llmModels,
    onAgentsChange,
    onLoadModels: _onLoadModels,
}: AgentExecutionsEditorProps) => {
    const { t } = useTranslation();
    const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

    const domainKeys = agents.map((a) => a.domainKey);
    const duplicates = domainKeys.filter(
        (key, i) => key && domainKeys.indexOf(key) !== i,
    );
    const allModels = Object.values(llmModels).flat();

    const handleAddAgent = () => {
        onAgentsChange([...agents, createEmptyAgent()]);
    };

    const handleRemoveAgent = (index: number) => {
        onAgentsChange(agents.filter((_, i) => i !== index));
        setDeleteIndex(null);
    };

    const updateAgent = (index: number, updated: AgentExecutionFormData) => {
        const next = [...agents];
        next[index] = updated;
        onAgentsChange(next);
    };

    return (
        <Box className="agent-executions-editor">
            <Box className="agent-executions-editor__header">
                <Box>
                    <Group gap="xs" align="center">
                        <IconSettingsAutomation size={20} />
                        <Text fw={700} size="lg">
                            {t("admin.flowConfigs.agents.title")}
                        </Text>
                        <Badge variant="filled" size="sm">{agents.length}</Badge>
                    </Group>
                    <Text size="sm" c="dimmed" className="agent-executions-editor__subtitle">
                        {t("admin.flowConfigs.agents.subtitle")}
                    </Text>
                </Box>

                <Button
                    leftSection={<IconPlus size={16} />}
                    size="sm"
                    onClick={handleAddAgent}
                >
                    {t("admin.flowConfigs.agents.addAgent")}
                </Button>
            </Box>

            {agents.length > 0 && (
                <Group gap="xs" className="agent-executions-editor__domain-chips">
                    {agents.map((agent, i) => (
                        <Badge
                            key={i}
                            variant="light"
                            color={agent.domainKey ? "blue" : "gray"}
                            size="md"
                        >
                            {agent.domainKey || `Agent ${i + 1}`}
                        </Badge>
                    ))}
                </Group>
            )}

            {agents.length === 0 && (
                <Card className="agent-executions-editor__empty-state" padding="xl" radius="md">
                    <Box className="agent-executions-editor__empty-icon">
                        <IconSettingsAutomation size={24} stroke={1.8} />
                    </Box>
                    <Text fw={600} size="md" ta="center">
                        {t("admin.flowConfigs.agents.noAgentsTitle")}
                    </Text>
                    <Text c="dimmed" ta="center" size="sm">
                        {t("admin.flowConfigs.agents.noAgents")}
                    </Text>
                    <Button
                        leftSection={<IconPlus size={16} />}
                        onClick={handleAddAgent}
                        variant="light"
                    >
                        {t("admin.flowConfigs.agents.addAgent")}
                    </Button>
                </Card>
            )}

            <Accordion
                multiple
                chevronPosition="left"
                className="agent-executions-editor__accordion"
            >
                {agents.map((agent, index) => {
                    const isDuplicate = duplicates.includes(agent.domainKey);

                    return (
                        <Accordion.Item
                            key={agent.domainKey || `agent-${index}`}
                            value={`agent-${index}`}
                        >
                            <Box style={{ display: "flex", alignItems: "center" }}>
                                <Accordion.Control style={{ flex: 1 }}>
                                    <Group gap="sm">
                                        <Badge
                                            variant="filled"
                                            color={agent.domainKey ? "blue" : "gray"}
                                            size="sm"
                                        >
                                            {index + 1}
                                        </Badge>
                                        <Text fw={500} size="sm">
                                            {agent.domainKey || `Agent ${index + 1}`}
                                        </Text>
                                        {agent.modelName && (
                                            <Badge variant="outline" size="xs" color="grape">
                                                {agent.modelName}
                                            </Badge>
                                        )}
                                    </Group>
                                </Accordion.Control>
                                <CloseButton
                                    color="red"
                                    size="sm"
                                    mr="sm"
                                    onClick={() => setDeleteIndex(index)}
                                    aria-label={t("admin.flowConfigs.agents.removeAgent")}
                                />
                            </Box>
                            <Accordion.Panel>
                                <Grid gutter="md">
                                    <Grid.Col span={6}>
                                        <TextInput
                                            label={t("admin.flowConfigs.agents.domainKey")}
                                            placeholder={t("admin.flowConfigs.agents.domainKeyPlaceholder")}
                                            value={agent.domainKey}
                                            onChange={(e) =>
                                                updateAgent(index, {
                                                    ...agent,
                                                    domainKey: e.currentTarget.value,
                                                })
                                            }
                                            error={
                                                isDuplicate
                                                    ? t("admin.flowConfigs.agents.duplicateDomainKey")
                                                    : undefined
                                            }
                                            required
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={6}>
                                        <Select
                                            label={t("admin.flowConfigs.agents.modelOverride")}
                                            description={t("admin.flowConfigs.agents.modelOverrideDesc")}
                                            data={allModels.map((m) => ({
                                                value: m.modelName,
                                                label: m.displayName || m.modelName,
                                            }))}
                                            value={agent.modelName || null}
                                            onChange={(val) =>
                                                updateAgent(index, {
                                                    ...agent,
                                                    modelName: val || "",
                                                })
                                            }
                                            searchable
                                            clearable
                                        />
                                    </Grid.Col>
                                </Grid>

                                <Divider
                                    my="md"
                                    label={
                                        <Group gap={4}>
                                            <IconPrompt size={14} />
                                            <Text size="xs">{t("admin.flowConfigs.agents.promptsSection")}</Text>
                                        </Group>
                                    }
                                    labelPosition="left"
                                />

                                <Textarea
                                    label={t("admin.flowConfigs.agents.systemPromptOverride")}
                                    description={t("admin.flowConfigs.agents.systemPromptOverrideDesc")}
                                    value={agent.systemPrompt}
                                    onChange={(e) =>
                                        updateAgent(index, {
                                            ...agent,
                                            systemPrompt: e.currentTarget.value,
                                        })
                                    }
                                    minRows={4}
                                    autosize
                                    maxRows={12}
                                    mb="sm"
                                />

                                <Textarea
                                    label={t("admin.flowConfigs.labels.userPrompt")}
                                    value={agent.userPrompt}
                                    onChange={(e) =>
                                        updateAgent(index, {
                                            ...agent,
                                            userPrompt: e.currentTarget.value,
                                        })
                                    }
                                    required
                                    minRows={4}
                                    autosize
                                    maxRows={12}
                                    mb="sm"
                                />

                                <Divider
                                    my="md"
                                    label={
                                        <Group gap={4}>
                                            <IconCode size={14} />
                                            <Text size="xs">{t("admin.flowConfigs.labels.responseSchema")}</Text>
                                        </Group>
                                    }
                                    labelPosition="left"
                                />

                                <Textarea
                                    label={t("admin.flowConfigs.labels.responseSchema")}
                                    value={agent.responseSchemaText}
                                    onChange={(e) => {
                                        const text = e.currentTarget.value;
                                        let parsed: Record<string, unknown> | null = null;
                                        try {
                                            parsed = text ? JSON.parse(text) : null;
                                        } catch {
                                            // Invalid JSON while typing - store text only
                                        }
                                        updateAgent(index, {
                                            ...agent,
                                            responseSchemaText: text,
                                            responseSchema: parsed ?? agent.responseSchema,
                                        });
                                    }}
                                    onBlur={() => {
                                        try {
                                            const parsed = agent.responseSchemaText
                                                ? JSON.parse(agent.responseSchemaText)
                                                : null;
                                            updateAgent(index, {
                                                ...agent,
                                                responseSchema: parsed,
                                            });
                                        } catch {
                                            // Leave as-is; validation on save
                                        }
                                    }}
                                    required
                                    minRows={6}
                                    autosize
                                    maxRows={20}
                                    placeholder='{"type": "object", "properties": {...}}'
                                    styles={{ input: { fontFamily: "monospace", fontSize: "0.8rem" } }}
                                />
                            </Accordion.Panel>
                        </Accordion.Item>
                    );
                })}
            </Accordion>

            <ConfirmModal
                opened={deleteIndex !== null}
                onClose={() => setDeleteIndex(null)}
                onConfirm={() => {
                    if (deleteIndex !== null) {
                        handleRemoveAgent(deleteIndex);
                    }
                }}
                title={t("admin.flowConfigs.agents.removeAgent")}
                message={t("admin.flowConfigs.agents.deleteConfirm")}
                confirmLabel={t("common.confirm")}
                danger
            />
        </Box>
    );
};
